import { createApiIdentityPlugin } from "zudoku/plugins";
import type { ApiIdentity } from "zudoku";
import type { ZudokuContext } from "zudoku";

const API_KEY_STORAGE_KEY = "beam:playground:api-key";
const USER_TOKEN_IDENTITY = "user-access-token";
const CUSTOM_API_KEY_IDENTITY = "custom-api-key";
const CUSTOM_API_KEY_HEADER = "X-Api-Key";
const CUSTOM_INPUT_DATA_ATTR = "data-beam-custom-api-key-input";
const PATCHED_ROW_DATA_ATTR = "data-beam-patched";
const HEADER_NAME_SELECTOR = `input[disabled][value="${CUSTOM_API_KEY_HEADER}"]`;

let cachedApiKey: string | null | undefined;

const isBrowser = (): boolean => typeof window !== "undefined";

const getStoredApiKey = (): string | null => {
	if (!isBrowser()) return null;
	return window.localStorage.getItem(API_KEY_STORAGE_KEY);
};

const setStoredApiKey = (value: string | null) => {
	if (!isBrowser()) return;
	if (!value) {
		window.localStorage.removeItem(API_KEY_STORAGE_KEY);
		cachedApiKey = null;
		return;
	}
	window.localStorage.setItem(API_KEY_STORAGE_KEY, value);
	cachedApiKey = value;
};

const readApiKey = (): string | null => {
	if (cachedApiKey !== undefined) {
		return cachedApiKey;
	}
	const stored = getStoredApiKey();
	cachedApiKey = stored ?? null;
	return cachedApiKey;
};

const patchLockedHeaderRow = (nameInput: HTMLInputElement): void => {
	const row = nameInput.parentElement as HTMLElement | null;
	if (!row) return;

	// Skip if already patched and custom input still exists
	if (
		row.hasAttribute(PATCHED_ROW_DATA_ATTR) &&
		row.querySelector(`input[${CUSTOM_INPUT_DATA_ATTR}]`)
	) {
		return;
	}

	// Set sentinel first to prevent MutationObserver re-entry
	row.setAttribute(PATCHED_ROW_DATA_ATTR, "true");

	row.classList.remove("cursor-not-allowed", "opacity-50");
	row.style.pointerEvents = "auto";

	const valueContainer = nameInput.nextElementSibling as HTMLElement | null;
	if (!valueContainer) return;

	valueContainer.innerHTML = "";
	const input = document.createElement("input");
	input.type = "text";
	input.setAttribute(CUSTOM_INPUT_DATA_ATTR, "true");
	input.placeholder = "Paste your API key";
	input.className =
		"w-full truncate border-0 p-0 m-0 shadow-none text-xs focus:outline-none focus-visible:outline-none focus-visible:ring-0 font-mono";
	input.autocomplete = "off";
	input.style.outline = "none";
	input.style.backgroundColor = "transparent";
	input.style.caretColor = "currentColor";

	const storedValue = readApiKey() ?? "";
	input.value = storedValue;

	input.addEventListener("input", (event) => {
		const target = event.target as HTMLInputElement;
		apiKeyStore.set(target.value);
	});

	valueContainer.appendChild(input);
};

const patchAllLockedHeaders = (): void => {
	if (!isBrowser()) return;
	const headerNameInputs =
		document.querySelectorAll<HTMLInputElement>(HEADER_NAME_SELECTOR);
	headerNameInputs.forEach(patchLockedHeaderRow);
};

const syncInputValues = (value: string): void => {
	if (!isBrowser()) return;
	const inputs = document.querySelectorAll<HTMLInputElement>(
		`input[${CUSTOM_INPUT_DATA_ATTR}]`,
	);
	inputs.forEach((input) => {
		if (input.value !== value) {
			input.value = value;
		}
	});
};

const apiKeyStore = {
	get(): string | null {
		return readApiKey();
	},
	set(value?: string | null): string | null {
		const trimmed = typeof value === "string" ? value.trim() : "";
		if (!trimmed) {
			setStoredApiKey(null);
			syncInputValues("");
			return null;
		}
		setStoredApiKey(trimmed);
		syncInputValues(trimmed);
		return trimmed;
	},
	clear(): null {
		setStoredApiKey(null);
		syncInputValues("");
		return null;
	},
} as const;

let storeRegistered = false;

const registerApiKeyStore = () => {
	if (typeof window === "undefined") return;
	if (
		storeRegistered &&
		(
			window as typeof window & {
				__beamPlaygroundApiKeyStore?: typeof apiKeyStore;
			}
		).__beamPlaygroundApiKeyStore === apiKeyStore
	) {
		return;
	}
	(
		window as typeof window & {
			__beamPlaygroundApiKeyStore?: typeof apiKeyStore;
		}
	).__beamPlaygroundApiKeyStore = apiKeyStore;
	storeRegistered = true;

	// Set up MutationObserver to patch locked header rows whenever React renders them
	const observer = new MutationObserver((mutations) => {
		const hasAddedNodes = mutations.some((m) => m.addedNodes.length > 0);
		if (!hasAddedNodes) return;
		patchAllLockedHeaders();
	});
	observer.observe(document.body, { childList: true, subtree: true });

	// Patch any already-present headers
	patchAllLockedHeaders();
};

registerApiKeyStore();

const createJwtIdentity = (context: ZudokuContext): ApiIdentity | null => {
	return {
		id: USER_TOKEN_IDENTITY,
		label: "Signed-in User JWT",
		authorizeRequest: async (request: Request) => {
			try {
				const signed = await context.signRequest(request);
				const authorization = signed.headers.get("Authorization");
				if (authorization) {
					request.headers.set("Authorization", authorization);
				}
			} catch (error) {
				console.warn(
					"[apiIdentities] Failed to sign request with user token",
					error,
				);
			}
			return request;
		},
	};
};

const createApiKeyIdentity = (): ApiIdentity => {
	registerApiKeyStore();
	return {
		id: CUSTOM_API_KEY_IDENTITY,
		label: "Custom API Key",
		authorizationFields: {
			headers: [CUSTOM_API_KEY_HEADER],
		},
		authorizeRequest: async (request: Request) => {
			const existingValue = request.headers.get(CUSTOM_API_KEY_HEADER);
			if (existingValue && existingValue.trim()) {
				apiKeyStore.set(existingValue);
				return request;
			}

			const apiKey = apiKeyStore.get();
			if (!apiKey) {
				console.warn(
					"[apiIdentities] API key identity selected but no key provided",
				);
				return request;
			}

			request.headers.set(CUSTOM_API_KEY_HEADER, apiKey);
			return request;
		},
	};
};

const apiIdentitiesPluginDefinition = {
	initialize: () => {
		registerApiKeyStore();
	},
	async getIdentities(context) {
		const identities: ApiIdentity[] = [];

		const jwtIdentity = createJwtIdentity(context);
		if (jwtIdentity) {
			identities.push(jwtIdentity);
		}

		identities.push(createApiKeyIdentity());

		return identities;
	},
};

export const apiIdentitiesPlugin = createApiIdentityPlugin(
	apiIdentitiesPluginDefinition,
);
